import { useEffect, useMemo } from 'react'

import { EmployeeCreationTemplateFieldFragment } from 'generated/resources/graphql'
import { camelCase } from 'lodash'

import Selector from 'modules/core/components/Selector'
import i18n from 'modules/core/lib/i18n'

import { useCountryDataFieldChange } from 'modules/employees/components/UnifiedAddEmployeeModal/CountryDataInputsContext'

interface Props {
  field: EmployeeCreationTemplateFieldFragment
  value?: string
  onChange: (fieldName: string, valueId: string) => void
}

export const CountrySpecificField = ({ field, value, onChange }: Props) => {
  const updateCountryDataField = useCountryDataFieldChange()

  // Map fieldId to the country_data_field_name used by CountryDataInputsContext
  const countryDataFieldName = field.fieldId

  // Filter out archived options
  const activeOptions = useMemo(
    () => field.options?.filter((option) => !option.archivedAt) || [],
    [field.options]
  )

  // Sync country data input when value changes
  useEffect(() => {
    if (!countryDataFieldName || !updateCountryDataField) return

    const matchingOption = activeOptions.find((option) => option.optionId === value)
    const hasValidValue = !!value && !!matchingOption

    if (!activeOptions.length || (value && !hasValidValue)) {
      updateCountryDataField({
        fieldName: countryDataFieldName,
        integrationSource: null,
        valueId: null,
      })
    } else if (hasValidValue) {
      updateCountryDataField({
        fieldName: countryDataFieldName,
        valueId: value,
        integrationSource: matchingOption?.integrationSource ?? null,
      })
    } else if (!value) {
      // Explicitly clear stale context entry when value is blank/undefined
      updateCountryDataField({
        fieldName: countryDataFieldName,
        integrationSource: null,
        valueId: null,
      })
    }
  }, [activeOptions, countryDataFieldName, updateCountryDataField, value])

  if (field.fieldType !== 'option' || !activeOptions.length) return null

  return (
    <Selector.Popover
      blankLabel={i18n.t('employee_form.payroll_data.select_an_option')}
      placeholder={i18n.t('employee_form.payroll_data.select_an_option')}
      searchPlaceholder={field.label}
      inputStyle="badge"
      label={field.label}
      onChange={(newValue) => {
        const stringValue = newValue ? String(newValue) : ''
        onChange(camelCase(field.fieldId), stringValue)
        if (countryDataFieldName && updateCountryDataField) {
          const selectedOption = activeOptions.find(
            (option) => String(option.optionId) === stringValue
          )
          updateCountryDataField({
            fieldName: countryDataFieldName,
            valueId: stringValue || null,
            integrationSource: selectedOption?.integrationSource ?? null,
          })
        }
      }}
      value={value ? [value] : []}
      isMultiSelect={false}
    >
      {activeOptions.map((option) => (
        <Selector.Option
          key={option.optionId}
          value={option.optionId}
          label={option.label}
          searchValue={option.label}
        >
          {option.label}
        </Selector.Option>
      ))}
    </Selector.Popover>
  )
}
